/** 浏览器端文字渲染：把中文标签渲染成位图再嵌入 PDF。
 *
 * 为什么不用 PDF 字体：首版无法随站点打包完整的 CJK 字体（体积与许可成本），
 * 而 PDF 内置字体不含中文，直接写文本必然乱码。位图文字保证：
 * - 不可能乱码；
 * - 不增加字体依赖；
 * - 线稿仍是矢量，物理尺寸精确。
 * 代价：文字不是矢量可选中文本。已在 docs/decisions 中记录。
 */
import type { RasterText, TextBlockRenderer } from './pdf';

const FONT_STACK = '"Noto Sans SC", "Source Han Sans SC", "Microsoft YaHei", "PingFang SC", "Hiragino Sans GB", sans-serif';
const SUPERSAMPLE = 6;

export function createCanvasTextRenderer(): TextBlockRenderer {
  return {
    async render(text: string, fontPt: number, weight: 'normal' | 'bold' = 'normal'): Promise<RasterText | null> {
      if (!text.trim()) return null;
      const lines = text.split('\n');
      const fontPx = fontPt * SUPERSAMPLE;
      const measureCanvas = document.createElement('canvas');
      const measureCtx = measureCanvas.getContext('2d');
      if (!measureCtx) return null;
      measureCtx.font = `${weight === 'bold' ? '700' : '400'} ${fontPx}px ${FONT_STACK}`;
      let maxWidth = 0;
      for (const line of lines) maxWidth = Math.max(maxWidth, measureCtx.measureText(line).width);

      const padX = Math.ceil(fontPx * 0.12);
      const lineHeight = Math.ceil(fontPx * 1.28);
      const width = Math.ceil(maxWidth) + padX * 2;
      const height = lineHeight * lines.length + Math.ceil(fontPx * 0.16);

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(2, width);
      canvas.height = Math.max(2, height);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${weight === 'bold' ? '700' : '400'} ${fontPx}px ${FONT_STACK}`;
      ctx.fillStyle = '#000000';
      ctx.textBaseline = 'top';
      lines.forEach((line, i) => {
        ctx.fillText(line, padX, Math.ceil(fontPx * 0.08) + i * lineHeight);
      });

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
      if (!blob) return null;
      const buffer = new Uint8Array(await blob.arrayBuffer());
      return {
        png: buffer,
        widthPt: canvas.width / SUPERSAMPLE,
        heightPt: canvas.height / SUPERSAMPLE,
      };
    },
  };
}